/* eslint-disable */
import React, { useState } from "react";
import {
  Card,
  Title,
  Text,
  Center,
  Group,
  Button,
  createStyles,
  RingProgress,
  Stack,
  Box,
  Loader,
} from "@mantine/core";
import { IconFingerprint } from "@tabler/icons-react";
import { useGetTimeUtilization } from "api/attendance/gettodayTimeUtilization";
import {
  Location,
  PunchInPayload,
  useAddPunchIn,
} from "api/attendance/punchIn";
import { getDeviceId } from "utils/deviceid";
import { getCurrentLocation } from "utils/devicegeolocation";
import PunchOutModal from "features/attendance-employees/components/punchOutModal";

const useStyles = createStyles((theme) => ({
  wrapper: {
    borderRadius: 8,
    border: "2px solid #f97316",
    padding: 22,
    background: "linear-gradient(180deg, #fff 0%, #fff8f3 100%)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },
  heading: {
    color: "#6b7280",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 16,
  },
  datetime: {
    textAlign: "center",
    fontSize: 18,
    fontWeight: 800,
    color: "#0f172a",
    marginTop: 6,
  },
  chartWrap: {
    display: "flex",
    justifyContent: "center",
    marginTop: 18,
  },
  ringInner: {
    width: 120,
    height: 120,
    borderRadius: 999,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "inset 0 0 0 6px rgba(0,0,0,0.02)",
  },
  totalLabel: {
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 1,
  },
  totalTime: {
    fontSize: 20,
    fontWeight: 800,
    marginTop: 6,
    color: "#0f172a",
  },
  productionPill: {
    display: "inline-block",
    background: "#111827",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 14,
    marginTop: 14,
  },
  punchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    justifyContent: "center",
    marginTop: 12,
    color: "#1f2937",
    fontWeight: 600,
  },
  punchIcon: {
    color: "#f97316",
  },
  punchButton: {
    marginTop: 18,
    background: "#f97316",
    color: "#fff",
    width: "100%",
    borderRadius: 8,
    padding: "12px 16px",
    fontWeight: 700,
    border: "none",
    boxShadow: "0 6px 0 rgba(249,115,22,0.12)",
    "&:hover": {
      background: "#ef7a21",
    },
  },
}));

function parseHMS(hms?: string | null): number | null {
  if (!hms) return null;
  const parts = hms.split(":").map((p) => p.trim());
  if (parts.length === 0) return null;
  if (parts.length === 3) {
    const [hh, mm, ss] = parts.map(Number);
    if (Number.isFinite(hh) && Number.isFinite(mm) && Number.isFinite(ss)) {
      return hh * 3600 + mm * 60 + ss;
    }
  } else if (parts.length === 2) {
    const [a, b] = parts.map(Number);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return a * 3600 + b * 60;
    }
  } else if (parts.length === 1) {
    const v = Number(parts[0]);
    if (Number.isFinite(v)) return v;
  }
  return null;
}

function formatSecondsToHMS(totalSeconds?: number | null) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return "0:00:00";
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const hhStr = String(hh);
  const mmStr = mm.toString().padStart(2, "0");
  const ssStr = ss.toString().padStart(2, "0");
  return `${hhStr}:${mmStr}:${ssStr}`;
}

function extractTotalSeconds(data: any): number | null {
  if (!data) return null;
  if (typeof data.totalWorkedSeconds === "number")
    return data.totalWorkedSeconds;
  if (typeof data.totalSeconds === "number") return data.totalSeconds;
  if (typeof data.totalWorkedTime === "number") return data.totalWorkedTime;
  if (typeof data.totalHours === "number")
    return Math.round(data.totalHours * 3600);
  if (typeof data.totalWorkedTime === "string") {
    const parsed = parseHMS(data.totalWorkedTime);
    if (parsed != null) return parsed;
  }
  if (typeof data.totalTime === "string") {
    const parsed = parseHMS(data.totalTime);
    if (parsed != null) return parsed;
  }
  if (typeof data.workingSeconds === "number") return data.workingSeconds;
  if (typeof data.workedSeconds === "number") return data.workedSeconds;
  return null;
}

export default function AttendanceWidget() {
  const { classes } = useStyles();

  const { data, isLoading } = useGetTimeUtilization();
  const { mutateAsync: addPunchIn } = useAddPunchIn();
  const [opened, setOpened] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);

  const payloadData = data?.data ?? null;
  const workingPercentage: number = payloadData?.workingPercentage ?? 0;
  const breakTime = payloadData?.breakTime ?? 0;
  const overtime = payloadData?.overtime ?? 0;
  const punchInTimeRaw: string | null = payloadData?.punchInTime ?? null;
  const canPunchOut: boolean = Boolean(payloadData?.canPunchOut);

  const totalSeconds = extractTotalSeconds(payloadData); 

  const totalTime =
    totalSeconds != null ? formatSecondsToHMS(totalSeconds) : "0:00:00";

  let productionHoursDisplay = "0.00 hrs";
  if (typeof workingPercentage === "number" && totalSeconds != null) {
    const prodSeconds = Math.round((workingPercentage / 100) * totalSeconds);
    const prodHours = prodSeconds / 3600;
    productionHoursDisplay = `${prodHours.toFixed(2)} hrs`;
  }

  const punchIn = punchInTimeRaw
    ? new Date(punchInTimeRaw).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "--:--";

  const dateLabel = new Date().toLocaleString("en-GB", {
    // hour: "2-digit",
    // minute: "2-digit",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const handlePunchIn = async () => {
    setLoadingAction(true);
    try {
      const timestamp = new Date().toISOString();
      const deviceId = getDeviceId();

      let location: Location = { lat: 0, lng: 0 };
      try {
        const { lat, lng } = await getCurrentLocation();
        location = { lat, lng };
      } catch (geoError) {
        console.log("location error", geoError);
      }

      const payload: PunchInPayload = {
        punchInTime: timestamp,
        source: "Web",
        deviceId,
        location,
      };

      await addPunchIn(payload);
    } catch (err) {
      console.error("Clock In Failed:", err);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <Card className={classes.wrapper} radius="md" p={0}>
      <div style={{ padding: 8 }}>
        <div>
          <div className={classes.heading}>Attendance</div>
          <div className={classes.datetime}>{dateLabel}</div>
        </div>

        <div className={classes.chartWrap}>
          {isLoading ? (
            <Loader />
          ) : (
            <RingProgress
              size={170}
              thickness={10}
              sections={[{ value: workingPercentage, color: "#10b981" }]}
              roundCaps
              styles={{
                root: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
              }}
              label={
                <div className={classes.ringInner}>
                  <Text className={classes.totalLabel}>Working</Text>
                  <Text className={classes.totalTime}>{workingPercentage}</Text>
                </div>
              }
            />
          )}
        </div>

        <Center>
          <div className={classes.productionPill}>
            Production : {productionHoursDisplay}
          </div>
        </Center>

        <div className={classes.punchRow}>
          <IconFingerprint size={18} className={classes.punchIcon} />
          <Text size="sm" color="dimmed">
            Clock In at {punchIn}
          </Text>
        </div>

        <Button
          className={classes.punchButton}
          radius="md"
          onClick={canPunchOut ? () => setOpened(true) : handlePunchIn}
          disabled={loadingAction || isLoading}
        >
          {loadingAction ? (
            <Loader size="xs" />
          ) : canPunchOut ? (
            "Clock Out"
          ) : (
            "Clock In"
          )}
        </Button>

        {!isLoading && (
          <Group position="apart" mt="md" spacing="xs">
            <Box>
              <Text size="xs" color="dimmed">
                Break
              </Text>
              <Text weight={700}>{String(breakTime)}</Text>
            </Box>
            <Box>
              <Text size="xs" color="dimmed">
                Overtime
              </Text>
              <Text weight={700}>{String(overtime)}</Text>
            </Box>
            <Box>
              <Text size="xs" color="dimmed">
                Working %
              </Text>
              <Text weight={700}>{workingPercentage ?? 0}%</Text>
            </Box>
          </Group>
        )}
      </div>

      <PunchOutModal opened={opened} onClose={() => setOpened(false)} />
    </Card>
  );
}
